import type { TrpcRouter } from '@rolebase/backend'
import { createTRPCClient, httpBatchLink } from '@trpc/client'
import settings from 'src/settings'

// 🔴 A SECOND tRPC client, on purpose. Do not "factor this out" with
// src/trpc.ts — their separation IS the guarantee.
//
// The app's client calls getValidAccessToken() on every request, and that
// helper SIGNS THE USER OUT (nhost.auth.signOut) when the stored session turns
// out to be invalid. Reporting an error through it could therefore log someone
// out — and the case where it would bite is the worst one: if the error came
// from a dead session, we would throw the person from the "Oups" screen
// straight to the login page, for having hit a bug.
//
// An error reporter must be inert. No auth header, no session refresh, no side
// effect. The procedure is public, so no token is needed to be heard.
const errorTrpc = createTRPCClient<TrpcRouter>({
  links: [httpBatchLink({ url: settings.backendUrl })],
})

// Anti-flood guard (ENR09-D11). A component that crashes, remounts and crashes
// again produces thousands of reports in minutes: unreadable journal, and on
// UdN a saturated quota. Past the cap we stop sending and keep COUNTING — the
// next allowed report carries how many were dropped, because "4000 identical
// errors dropped" is information and silence is not.
const WINDOW_MS = 60_000
const MAX_PER_WINDOW = 10

let windowStartedAt = 0
let sentInWindow = 0
let droppedSinceLastSend = 0

function allowThrough(now: number): boolean {
  if (now - windowStartedAt > WINDOW_MS) {
    windowStartedAt = now
    sentInWindow = 0
  }

  if (sentInWindow >= MAX_PER_WINDOW) {
    droppedSinceLastSend++
    return false
  }

  sentInWindow++
  return true
}

// Exported for the tests: they own the counters, so each case starts clean.
export function resetErrorReportingState() {
  windowStartedAt = 0
  sentInWindow = 0
  droppedSinceLastSend = 0
  sdkLoading = undefined
  listenersInstalled = false
}

// 🔴 THE FORBIDDEN LIST, and it is not decoration.
//
// NEW-06 (11/08/2026) tore Sentry out of this app because replayIntegration()
// was replaying 10% of ALL sessions and 100% of the ones with an error, to
// sentry.lonestone.io — a third party's server — on a tool that carries private
// tensions. That was the whole reason. The SDK comes back here pointed at a
// server of ours, and ONLY to capture errors.
//
// Never add: session replay, tracing (tracesSampleRate), profiling
// (profilesSampleRate). errorReporting.test.ts fails if you do, on purpose.
// That test guards a decision (ENR09-D7) — don't "fix" it by allowing what it
// forbids.
export function buildSdkOptions(dsn: string) {
  if (!dsn) return undefined
  return { dsn, integrations: [] }
}

let sdkLoading: Promise<unknown> | undefined

// Dynamic import so that an instance without a DSN never loads the package at
// all: Yocoop's bundle does not carry it. "No line assumes GlitchTip exists"
// (ENR09-D18) then holds at runtime, not merely in intent.
function ensureSdk(dsn: string) {
  if (sdkLoading) return sdkLoading

  const options = buildSdkOptions(dsn)
  if (!options) return undefined

  sdkLoading = import('@sentry/react')
    .then((Sentry) => {
      Sentry.init(options)
      return Sentry
    })
    .catch(() => {
      // The external book is a bonus, never a dependency: if it cannot load,
      // the report still reaches our own server below.
      //
      // sdkLoading is deliberately NOT reset here: one failed load disables the
      // external destination for the rest of the session. Retrying on every
      // error would mean one failed network request per error, exactly when the
      // app is already in trouble — and the server journal keeps working.
      return undefined
    })

  return sdkLoading
}

export function reportError(error: Error) {
  if (!allowThrough(Date.now())) return

  const dropped = droppedSinceLastSend
  droppedSinceLastSend = 0

  // 🔴 Three fields, and nothing else (ENR09-D5): never the store, never the
  // URL query string (it carries ids), never a form body. location.pathname
  // only — the path says which screen broke, the query string would say who.
  const payload = {
    message:
      dropped > 0 ? `${error.message} [+${dropped} ignorées]` : error.message,
    stack: error.stack,
    // Guarded rather than assumed: this module must not be the thing that
    // throws. Reading window blindly would make the reporter fail wherever
    // there is no DOM, which is the one failure mode it cannot afford.
    page: typeof window === 'undefined' ? undefined : window.location.pathname,
  }

  // Never throws. An error raised by the error reporter would loop straight
  // back into the boundary, and the white screen would return through the very
  // door this feature closes.
  errorTrpc.clientError.report.mutate(payload).catch(() => {
    // Losing a report is better than losing the screen.
  })

  // The external destination ADDS to our server, it never replaces it. Empty
  // DSN (Yocoop, local, staging) means this does nothing and loads nothing.
  try {
    ensureSdk(settings.errorReporting.dsn)?.then((Sentry) => {
      ;(
        Sentry as { captureException?: (e: Error) => void }
      )?.captureException?.(error)
    })
  } catch {
    // Same rule as above: the reporter is inert, whatever happens.
  }
}

// Whatever a browser hands us, turned into an Error. A rejected promise carries
// a `reason` that can be anything at all — a string, an object, undefined — and
// only an Error has the stack that makes a report worth reading.
function toError(value: unknown): Error {
  if (value instanceof Error) return value
  if (typeof value === 'string') return new Error(value)
  try {
    return new Error(JSON.stringify(value))
  } catch {
    // Circular structures, getters that throw… the shape is already telling.
    return new Error('Unserializable rejection')
  }
}

let listenersInstalled = false

// The boundary only catches RENDER errors. In production most failures are not
// that: a network call that fails, a .then() with no .catch(), an async handler
// that throws. Without these two listeners the incident book would miss the
// common case and keep only the rare one (ENR09-D19, added 15/08 after review).
//
// The anti-flood guard above covers these too, so a loop of rejections cannot
// drown the journal.
export function configureErrorReporting() {
  if (listenersInstalled || typeof window === 'undefined') return
  listenersInstalled = true

  window.addEventListener('error', (event) => {
    reportError(toError(event.error ?? event.message))
  })

  window.addEventListener('unhandledrejection', (event) => {
    reportError(toError(event.reason))
  })
}
